import React, { useCallback, useEffect, useState } from 'react';

import { DateTime } from 'luxon';
import { createUseStyles } from 'react-jss';

import Text from 'DesignSystem2/Text';
import { Palette, Spacing } from 'DesignSystem2/Theme';

import SelectInput from './SelectInput';
import TextInput from './TextInput';

const MONTHS = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
];

const useStyles = createUseStyles({
    inputContainer: {
        display: 'flex',
        gap: Spacing(1),
        alignItems: 'flex-start',
    },
    monthSelect: {
        border: '1px solid',
        borderColor: Palette.Fill.x50,
        borderRadius: '8px',
        flex: 2,
    },
    dayInput: {
        border: '1px solid',
        borderColor: Palette.Fill.x50,
        borderRadius: '8px',
        flex: 1,
    },
    yearInput: {
        border: '1px solid',
        borderColor: Palette.Fill.x50,
        borderRadius: '8px',
        flex: 1,
    },
    wrapper: {
        width: '100%',
    },
    label: {
        display: 'block',
        marginBottom: Spacing(0.5),
    },
    errorText: {
        marginTop: Spacing(0.5),
    },
});

export interface BirthdayInputProps {
    month: string;
    day: string;
    year: string;
    onChange: (type: 'month' | 'day' | 'year', value: string) => void;
    onValidate?: (isValid: boolean, errorMessage: string, date?: Date) => void;
    minAge?: number;
    errorText?: string;
    showError?: boolean;
    labelText?: string;
    className?: string;
    validateOnChange?: boolean;
}

const BirthdayInput: React.FC<BirthdayInputProps> = ({
    month,
    day,
    year,
    onChange,
    onValidate,
    minAge = 18,
    errorText,
    showError = false,
    labelText,
    className,
    validateOnChange = true,
}) => {
    const classes = useStyles();
    const [localErrorMessage, setLocalErrorMessage] = useState('');

    const validateDate = useCallback(() => {
        if (!year || !month || !day) {
            const errorMsg = 'Enter a valid birthday';
            setLocalErrorMessage(errorMsg);
            onValidate && onValidate(false, errorMsg);
            return;
        }

        const isDateInvalid = year.length < 4 || month.length < 1 || day.length < 1;
        if (isDateInvalid) {
            const errorMsg = 'Enter a valid birthday';
            setLocalErrorMessage(errorMsg);
            onValidate && onValidate(false, errorMsg);
            return;
        }

        const dateTime = DateTime.utc(parseInt(year), parseInt(month), parseInt(day));
        if (!dateTime.isValid) {
            const errorMsg = 'Enter a valid birthday';
            setLocalErrorMessage(errorMsg);
            onValidate && onValidate(false, errorMsg);
            return;
        }

        const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));

        if (date > new Date()) {
            const errorMsg = 'Enter a valid birthday';
            setLocalErrorMessage(errorMsg);
            onValidate && onValidate(false, errorMsg);
            return;
        }

        const age = Math.abs(dateTime.diffNow('years').years);

        const isAboveAge = age > 120;
        if (isAboveAge) {
            const errorMsg = 'Enter a valid birthday';
            setLocalErrorMessage(errorMsg);
            onValidate && onValidate(false, errorMsg);
            return;
        }

        const isUnderAge = age < minAge;
        if (isUnderAge) {
            const errorMsg = minAge === 18 ? 'You must be 18 years or older' : `You must be ${minAge} years or older`;
            setLocalErrorMessage(errorMsg);
            onValidate && onValidate(false, errorMsg);
            return;
        }

        setLocalErrorMessage('');
        onValidate && onValidate(true, '', date);
        return date;
    }, [year, month, day, minAge, onValidate]);

    useEffect(() => {
        if (validateOnChange && (month || day || year)) {
            validateDate();
        }
    }, [month, day, year, validateDate, validateOnChange]);

    const handleInputChange = (value: string, type: 'month' | 'day' | 'year'): void => {
        onChange(type, value);
    };

    const displayError = showError && (errorText || localErrorMessage);
    const errorMessage = errorText || localErrorMessage;

    return (
        <div className={`${classes.wrapper} ${className || ''}`}>
            {labelText && <Text.Body.x20 className={classes.label}>{labelText}</Text.Body.x20>}
            <div className={classes.inputContainer}>
                <div className={classes.monthSelect}>
                    <SelectInput
                        value={month}
                        onChange={(e): void => handleInputChange(e.target.value, 'month')}
                        errorText={displayError ? errorMessage : undefined}
                    >
                        <option value={''} disabled>
                            {'Month'}
                        </option>
                        {MONTHS.map((monthOption) => (
                            <option key={monthOption.value} value={monthOption.value}>
                                {monthOption.label}
                            </option>
                        ))}
                    </SelectInput>
                </div>
                <div className={classes.dayInput}>
                    <TextInput
                        value={day}
                        onChange={(e): void => handleInputChange(e.target.value, 'day')}
                        placeholder={'Day'}
                        type={'number'}
                        min={1}
                        max={31}
                        errorText={displayError ? errorMessage : undefined}
                    />
                </div>
                <div className={classes.yearInput}>
                    <TextInput
                        value={year}
                        onChange={(e): void => handleInputChange(e.target.value, 'year')}
                        placeholder={'Year'}
                        type={'number'}
                        min={1900}
                        max={new Date().getFullYear()}
                        errorText={displayError ? errorMessage : undefined}
                    />
                </div>
            </div>
            {displayError && (
                <Text.Body.x20 color={Palette.Critical.x10} className={classes.errorText}>
                    {errorMessage}
                </Text.Body.x20>
            )}
        </div>
    );
};

export default BirthdayInput;
